import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  ISignUpResult
} from 'amazon-cognito-identity-js'
import AWS from 'aws-sdk'
import awsConfig from '../config/aws-config'

const userPool = new CognitoUserPool({
  UserPoolId: awsConfig.userPoolId,
  ClientId: awsConfig.userPoolWebClientId
})

// Configure AWS SDK
AWS.config.region = awsConfig.region
const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
  region: awsConfig.region
})

interface SignInResult {
  session?: string
  username?: string
  challengeParameters?: AWS.CognitoIdentityServiceProvider.ChallengeParametersType
}

interface VerifyOTPSuccess {
  success: true
  tokens: AWS.CognitoIdentityServiceProvider.AuthenticationResultType
}

interface VerifyOTPRetry {
  success: false
  session: string
  message: string
}

type VerifyOTPResult = VerifyOTPSuccess | VerifyOTPRetry

interface PasswordAuthResult {
  success: boolean
  tokens?: AWS.CognitoIdentityServiceProvider.AuthenticationResultType
}

class AuthService {
  // Temporary password for signup (required by Cognito)
  TEMP_PASSWORD = 'Temp@Pass123!'

  // Sign up with email/phone and send OTP
  signUp(
    username: string,
    name: string,
    email: string | null,
    phoneNumber: string | null
  ): Promise<ISignUpResult> {
    return new Promise((resolve, reject) => {
      const attributeList: CognitoUserAttribute[] = []
      let cognitoUsername = username

      // Add name attribute
      if (name) {
        attributeList.push(
          new CognitoUserAttribute({ Name: 'name', Value: name })
        )
      }

      if (email) {
        attributeList.push(
          new CognitoUserAttribute({ Name: 'email', Value: email })
        )
        // If email is provided and no explicit username, use email as username
        if (!username) {
          cognitoUsername = email
        }
      }

      if (phoneNumber) {
        // Format phone number for India: +91XXXXXXXXXX
        const formattedPhone = phoneNumber.startsWith('+91')
          ? phoneNumber
          : `+91${phoneNumber}`
        attributeList.push(
          new CognitoUserAttribute({ Name: 'phone_number', Value: formattedPhone })
        )
        // If phone is provided and no explicit username (or username is not formatted), use formatted phone as username
        if (!username || !username.startsWith('+')) {
          cognitoUsername = formattedPhone
        }
      }

      userPool.signUp(cognitoUsername, this.TEMP_PASSWORD, attributeList, [], (err, result) => {
        if (err) {
          reject(err)
          return
        }
        if (result) {
          resolve(result)
        }
      })
    })
  }

  // Verify OTP code
  confirmSignUp(username: string, code: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      })

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err)
          return
        }
        resolve(result)
      })
    })
  }

  // Sign in with password (used after signup confirmation)
  async signInWithPassword(username: string, password: string): Promise<PasswordAuthResult> {
    try {
      const params: AWS.CognitoIdentityServiceProvider.InitiateAuthRequest = {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: awsConfig.userPoolWebClientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password
        }
      }

      const response = await cognitoIdentityServiceProvider.initiateAuth(params).promise()

      if (response.AuthenticationResult) {
        // Success! Store tokens
        const tokens = response.AuthenticationResult
        localStorage.setItem('authToken', tokens.AccessToken || '')
        localStorage.setItem('idToken', tokens.IdToken || '')
        localStorage.setItem('refreshToken', tokens.RefreshToken || '')

        return {
          success: true,
          tokens: tokens
        }
      } else {
        throw new Error('No authentication result received')
      }
    } catch (error) {
      console.error('Error signing in with password:', error)
      throw error
    }
  }

  // Sign in with custom auth flow (OTP) using AWS SDK
  async signInWithOTP(username: string): Promise<SignInResult> {
    try {
      const params: AWS.CognitoIdentityServiceProvider.InitiateAuthRequest = {
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: awsConfig.userPoolWebClientId,
        AuthParameters: {
          USERNAME: username
        }
      }

      const response = await cognitoIdentityServiceProvider.initiateAuth(params).promise()

      if (response.Session) {
        // Return session token for OTP verification
        return {
          session: response.Session,
          username: username,
          challengeParameters: response.ChallengeParameters
        }
      } else {
        throw new Error('No session token received')
      }
    } catch (error) {
      console.error('Error sending OTP:', error)
      throw error
    }
  }

  // Verify OTP for sign in using AWS SDK
  async verifyOTP(
    session: string | null,
    username: string,
    otp: string
  ): Promise<VerifyOTPResult> {
    try {
      const params: AWS.CognitoIdentityServiceProvider.RespondToAuthChallengeRequest = {
        ChallengeName: 'CUSTOM_CHALLENGE',
        ClientId: awsConfig.userPoolWebClientId,
        ChallengeResponses: {
          USERNAME: username,
          ANSWER: otp
        },
        Session: session || undefined
      }

      const response = await cognitoIdentityServiceProvider.respondToAuthChallenge(params).promise()

      if (response.AuthenticationResult) {
        // Success! Store tokens
        const tokens = response.AuthenticationResult
        localStorage.setItem('authToken', tokens.AccessToken || '')
        localStorage.setItem('idToken', tokens.IdToken || '')
        localStorage.setItem('refreshToken', tokens.RefreshToken || '')

        return {
          success: true,
          tokens: tokens
        }
      } else if (response.Session) {
        // Wrong OTP, but can retry
        return {
          success: false,
          session: response.Session,
          message: 'Invalid OTP code. Please try again.'
        }
      } else {
        throw new Error('Unexpected response from server')
      }
    } catch (error) {
      console.error('Error verifying OTP:', error)
      throw error
    }
  }

  // Resend OTP for sign up
  resendConfirmationCode(username: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      })

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err)
          return
        }
        resolve(result)
      })
    })
  }

  // Resend OTP for sign in (reinitiate custom auth)
  resendSignInOTP(username: string): Promise<SignInResult> {
    return this.signInWithOTP(username)
  }

  // Get current user
  getCurrentUser(): CognitoUser | null {
    return userPool.getCurrentUser()
  }

  // Get user session
  getUserSession(): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser()

      if (!cognitoUser) {
        reject(new Error('No user found'))
        return
      }

      cognitoUser.getSession((err: Error | null, session: unknown) => {
        if (err) {
          reject(err)
          return
        }
        resolve(session)
      })
    })
  }

  // Sign out (local cleanup only)
  signOut(): void {
    const cognitoUser = this.getCurrentUser()
    if (cognitoUser) {
      cognitoUser.signOut()
    }
    localStorage.removeItem('authToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('refreshToken')
  }

  // Get the Cognito logout URL to clear the hosted UI session
  getCognitoLogoutUrl(): string {
    const { oauth, userPoolWebClientId } = awsConfig

    const params = new URLSearchParams({
      client_id: userPoolWebClientId,
      logout_uri: window.location.origin,
    })

    return `https://${oauth.domain}/logout?${params.toString()}`
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const authToken = localStorage.getItem('authToken')
    return authToken !== null
  }

  // Refresh access token using refresh token
  async refreshAccessToken(): Promise<AWS.CognitoIdentityServiceProvider.AuthenticationResultType> {
    try {
      const refreshToken = localStorage.getItem('refreshToken')

      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const params: AWS.CognitoIdentityServiceProvider.InitiateAuthRequest = {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: awsConfig.userPoolWebClientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken
        }
      }

      const response = await cognitoIdentityServiceProvider.initiateAuth(params).promise()

      if (response.AuthenticationResult) {
        const tokens = response.AuthenticationResult

        // Update tokens in localStorage
        if (tokens.AccessToken) {
          localStorage.setItem('authToken', tokens.AccessToken)
        }
        if (tokens.IdToken) {
          localStorage.setItem('idToken', tokens.IdToken)
        }
        // Note: Refresh token is not always returned in refresh flow
        // Only update if provided
        if (tokens.RefreshToken) {
          localStorage.setItem('refreshToken', tokens.RefreshToken)
        }

        return tokens
      } else {
        throw new Error('No authentication result received from refresh')
      }
    } catch (error) {
      console.error('Error refreshing token:', error)
      // Clear tokens on refresh failure
      this.signOut()
      throw error
    }
  }

  // Get token from localStorage
  getAccessToken(): string | null {
    return localStorage.getItem('authToken')
  }

  getIdToken(): string | null {
    return localStorage.getItem('idToken')
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken')
  }

  // OAuth / Social Login Methods

  // Get the Google login URL for Cognito Hosted UI
  getGoogleLoginUrl(): string {
    const { oauth, userPoolWebClientId } = awsConfig
    const redirectUri = `${window.location.origin}${oauth.callbackPath}`

    const params = new URLSearchParams({
      client_id: userPoolWebClientId,
      response_type: oauth.responseType,
      scope: oauth.scope.join(' '),
      redirect_uri: redirectUri,
      identity_provider: 'Google',
      prompt: 'login',
    })

    return `https://${oauth.domain}/oauth2/authorize?${params.toString()}`
  }

  // Initiate Google login by redirecting to Cognito Hosted UI
  initiateGoogleLogin(): void {
    window.location.href = this.getGoogleLoginUrl()
  }

  // Exchange authorization code for tokens
  async exchangeCodeForTokens(code: string): Promise<AWS.CognitoIdentityServiceProvider.AuthenticationResultType> {
    const { oauth, userPoolWebClientId } = awsConfig
    const redirectUri = `${window.location.origin}${oauth.callbackPath}`

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: userPoolWebClientId,
      code: code,
      redirect_uri: redirectUri
    })

    const response = await fetch(`https://${oauth.domain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: params.toString()
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error_description || 'Failed to exchange code for tokens')
    }

    const data = await response.json()

    // Store tokens in localStorage
    const tokens = {
      AccessToken: data.access_token,
      IdToken: data.id_token,
      RefreshToken: data.refresh_token,
      ExpiresIn: data.expires_in,
      TokenType: data.token_type
    }

    localStorage.setItem('authToken', tokens.AccessToken || '')
    localStorage.setItem('idToken', tokens.IdToken || '')
    if (tokens.RefreshToken) {
      localStorage.setItem('refreshToken', tokens.RefreshToken)
    }

    return tokens
  }

  // Get the OAuth callback URL
  getOAuthCallbackUrl(): string {
    return `${window.location.origin}${awsConfig.oauth.callbackPath}`
  }
}

export default new AuthService()
